import React from 'react';
import { Form, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@mui/material';
import { faLock, faLockOpen, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import type { Role, User } from 'app/types';
import { TranslationKeys } from 'app/translations';
import { enabledLocales } from 'app/components/Intl';
import { ActionButton, DetailsPanel } from 'app/components';
import { EditableFieldFactory } from 'app/components/FormikField/Factory/EditableFieldFactory';
import type { UserDetailsPanelFormValues } from '../../UserDetailsPanel.types';
import { UserDetailsPanelFields } from '../../UserDetailsPanel.types';
import { useOwnersQuery } from 'app/Domain/Users/Hooks';
import { UserBlockStatusChip } from 'app/Domain/Users/Components';
import { useUserDetailsPanelPermissions } from './useUserDetailsPanelPermissions';

type UserDetailsPanelFormProps = {
  isLoading?: boolean;
  isLoggedInUser?: boolean;
  disabledActionButtonsWhileLoading?: boolean;
  isEdit: boolean;
  setIsEdit: (flag: boolean) => void;
  roles: Role[];
  user: User | undefined;
  onBlockButtonClick: () => void;
  onUnblockButtonClick: () => void;
};

const FIELD_VARIANT = 'slim';

export const UserDetailsPanelForm = ({
  setIsEdit,
  isEdit,
  roles,
  user,
  onBlockButtonClick,
  onUnblockButtonClick,
  disabledActionButtonsWhileLoading = false,
  isLoggedInUser = false,
  isLoading = false,
}: UserDetailsPanelFormProps) => {
  const intl = useIntl();
  const { initialValues, values, setFieldValue, submitForm, resetForm } =
    useFormikContext<UserDetailsPanelFormValues>();

  const { canManageOwners, canEditOwner, canEditRole, canShowRoleAsDropdown, canBlockUser } =
    useUserDetailsPanelPermissions({
      user,
      isLoggedInUser,
    });

  const { data: owners } = useOwnersQuery();

  const ownersSelectItems = React.useMemo(() => {
    return owners?.data.map(owner => ({
      element: owner.ownerName,
      value: owner.ownerId,
    }));
  }, [owners]);

  const rolesSelectItems = React.useMemo(() => {
    return roles.map(role => ({
      element: role.name,
      value: role.roleId,
    }));
  }, [roles]);

  const languagesSelectItems = React.useMemo(() => {
    return Object.keys(enabledLocales).map(language => ({
      element: intl.formatMessage({ id: `translations.${language}` }),
      value: language,
    }));
  }, [intl]);

  const onClickEditButton = React.useCallback(() => {
    setIsEdit(true);
  }, [setIsEdit]);

  const onClickCancelButton = React.useCallback(() => {
    resetForm();
    setIsEdit(false);
  }, [resetForm, setIsEdit]);

  const onChangeOwner = React.useCallback(() => {
    setFieldValue(UserDetailsPanelFields.RoleId.InputName, '');
  }, [setFieldValue]);

  const onClickSubmit = React.useCallback(() => {
    const hasChangedValues = JSON.stringify(initialValues) !== JSON.stringify(values);

    if (hasChangedValues) {
      submitForm();
    } else {
      setIsEdit(false);
    }
  }, [initialValues, setIsEdit, submitForm, values]);

  const DetailsPanelColumns = React.useMemo(() => {
    const personalRows: Record<string, React.ReactNode> = {
      [UserDetailsPanelFields.FirstName.Label]: (
        <EditableFieldFactory
          editing={isEdit}
          value={initialValues.firstName}
          field={{
            variant: FIELD_VARIANT,
            name: UserDetailsPanelFields.FirstName.InputName,
            schema: UserDetailsPanelFields.FirstName.schema,
            required: true,
          }}
        />
      ),
      [UserDetailsPanelFields.MiddleName.Label]: (
        <EditableFieldFactory
          editing={isEdit}
          value={initialValues.middleName}
          field={{
            variant: FIELD_VARIANT,
            name: UserDetailsPanelFields.MiddleName.InputName,
          }}
        />
      ),
      [UserDetailsPanelFields.LastName.Label]: (
        <EditableFieldFactory
          editing={isEdit}
          value={initialValues.lastName}
          field={{
            variant: FIELD_VARIANT,
            name: UserDetailsPanelFields.LastName.InputName,
            schema: UserDetailsPanelFields.LastName.schema,
            required: true,
          }}
        />
      ),
      [UserDetailsPanelFields.EmailAddress.Label]: (
        <EditableFieldFactory
          editing={isEdit}
          value={initialValues.emailAddress}
          field={{
            variant: FIELD_VARIANT,
            name: UserDetailsPanelFields.EmailAddress.InputName,
            schema: UserDetailsPanelFields.EmailAddress.schema,
            required: true,
          }}
        />
      ),
      // Owner assignment is the only row restricted to admins; every other row always renders.
      ...(canManageOwners && {
        [UserDetailsPanelFields.OwnerId.Label]: (
          <EditableFieldFactory
            editing={false}
            value={initialValues.ownerId}
            onChange={onChangeOwner}
            field={{
              variant: FIELD_VARIANT,
              type: 'select',
              name: UserDetailsPanelFields.OwnerId.InputName,
              items: ownersSelectItems,
              schema: UserDetailsPanelFields.OwnerId.schema,
              disabled: !canEditOwner,
              required: true,
            }}
          />
        ),
      }),
      [UserDetailsPanelFields.RoleId.Label]: canShowRoleAsDropdown ? (
        <EditableFieldFactory
          editing={isEdit}
          value={user?.roleName || initialValues.roleId}
          field={{
            variant: FIELD_VARIANT,
            type: 'select',
            name: UserDetailsPanelFields.RoleId.InputName,
            items: rolesSelectItems,
            schema: UserDetailsPanelFields.RoleId.schema,
            disabled: !canEditRole,
            required: true,
          }}
        />
      ) : (
        user?.roleName
      ),
      [UserDetailsPanelFields.Language.Label]: (
        <EditableFieldFactory
          editing={isEdit}
          value={initialValues.language}
          field={{
            variant: FIELD_VARIANT,
            type: 'select',
            name: UserDetailsPanelFields.Language.InputName,
            items: languagesSelectItems,
            schema: UserDetailsPanelFields.Language.schema,
            required: true,
          }}
        />
      ),
      [UserDetailsPanelFields.CreatedAt.Label]: (
        <EditableFieldFactory
          editing={isEdit}
          value={initialValues.createdAt}
          field={{
            variant: FIELD_VARIANT,
            type: 'date',
            name: UserDetailsPanelFields.CreatedAt.InputName,
            disabled: true,
          }}
        />
      ),
      [UserDetailsPanelFields.Internal.Label]: (
        <EditableFieldFactory
          editing={isEdit}
          field={{ type: 'checkbox', variant: FIELD_VARIANT, name: UserDetailsPanelFields.Internal.InputName }}
          value={initialValues.internal}
        />
      ),
    };

    return [{ rows: personalRows }];
  }, [
    isEdit,
    initialValues.firstName,
    initialValues.middleName,
    initialValues.lastName,
    initialValues.emailAddress,
    initialValues.ownerId,
    initialValues.roleId,
    initialValues.language,
    initialValues.createdAt,
    initialValues.internal,
    canManageOwners,
    onChangeOwner,
    ownersSelectItems,
    canEditOwner,
    canShowRoleAsDropdown,
    rolesSelectItems,
    canEditRole,
    languagesSelectItems,
    user?.roleName,
  ]);

  const isUserBlocked = !!user?.isBlocked;

  const DetailsPanelActions = React.useMemo(() => {
    return (
      <>
        {!isEdit && (
          <Box sx={theme => ({ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) })}>
            <ActionButton
              messageId={TranslationKeys.global_edit}
              variant={'text'}
              icon={faPenToSquare}
              onClick={onClickEditButton}
              disabled={disabledActionButtonsWhileLoading}
            />
            <ActionButton
              messageId={isUserBlocked ? TranslationKeys.users_unblock : TranslationKeys.users_block}
              variant={'text'}
              icon={isUserBlocked ? faLockOpen : faLock}
              onClick={isUserBlocked ? onUnblockButtonClick : onBlockButtonClick}
              disabled={disabledActionButtonsWhileLoading || !canBlockUser}
            />
          </Box>
        )}
        {isEdit && (
          <Box sx={theme => ({ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) })}>
            <ActionButton
              messageId={TranslationKeys.button_cancel}
              onClick={onClickCancelButton}
              variant="outlined"
              disabled={disabledActionButtonsWhileLoading}
            />
            <ActionButton
              messageId={TranslationKeys.button_save}
              type="submit"
              onClick={onClickSubmit}
              disabled={disabledActionButtonsWhileLoading}
            />
          </Box>
        )}
      </>
    );
  }, [
    isEdit,
    onClickEditButton,
    disabledActionButtonsWhileLoading,
    isUserBlocked,
    onUnblockButtonClick,
    onBlockButtonClick,
    canBlockUser,
    onClickCancelButton,
    onClickSubmit,
  ]);

  return (
    <Form>
      <DetailsPanel
        loading={isLoading}
        header={<FormattedMessage id={TranslationKeys.users_detailInformation} />}
        headerButton={<UserBlockStatusChip user={user} />}
        columns={DetailsPanelColumns}
        actions={DetailsPanelActions}
      />
    </Form>
  );
};
